import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, ArrowRight } from "lucide-react";
import { sfx } from "@/lib/sounds";

export default function ServerCard({ server, index = 0 }) {
  const isOnline = server.status === "online";
  const themeColor = server.color || "#FFBF00";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      data-testid={`server-card-${server.key}`}
      className="group relative flex flex-col h-full overflow-hidden rounded-2xl sm:rounded-3xl bg-[#141414] border-2 sm:border-[3px] border-white/10 hover:border-[#FFBF00]/60 hover-blocky blocky-shadow transition-colors duration-300"
    >
      <Link
        to={`/servers/${server.key}`}
        onClick={() => sfx.click()}
        className="flex flex-col h-full w-full"
      >
        {/* Banner / Image Section */}
        <div className="relative w-full aspect-video sm:aspect-[16/9] overflow-hidden bg-black/40 shrink-0">
          <img
            src={server.image}
            alt={server.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
            loading="lazy"
          />
          {/* Subtle overlay for badge readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-black/20 to-black/40" />

          {/* Status Badge */}
          <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-full glass text-[11px] sm:text-xs font-mono font-medium backdrop-blur-md bg-black/50 text-white border border-white/10">
            <span
              className={`w-2 h-2 rounded-full ${
                isOnline ? "bg-green-500 pulse-dot" : "bg-red-500"
              }`}
            />
            {isOnline ? "ONLINE" : "OFFLINE"}
          </div>

          {/* Player Count Badge */}
          <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full glass text-[11px] sm:text-xs font-mono backdrop-blur-md bg-black/50 text-white border border-white/10">
            <Users size={12} className="text-[#FFBF00]" />
            <span>
              {server.players_online}/{server.max_players}
            </span>
          </div>
        </div>

        {/* Content Section - Expands dynamically */}
        <div className="flex flex-col flex-grow p-4 sm:p-6 justify-between bg-gradient-to-b from-[#141414] to-[#1a1a1a]">
          <div>
            {/* Tagline */}
            {server.tagline && (
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#FFBF00] font-bold truncate">
                {server.tagline}
              </div>
            )}

            {/* Title */}
            <h3 className="font-hero text-xl sm:text-2xl md:text-3xl mt-1 text-white font-bold tracking-tight line-clamp-1">
              {server.name}
            </h3>

            {/* Description */}
            <p className="mt-2 text-xs sm:text-sm text-white/70 leading-relaxed line-clamp-3">
              {server.description}
            </p>

            {/* Highlights Chips */}
            {(server.highlights || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5 sm:gap-2">
                {server.highlights.slice(0, 4).map((h) => (
                  <span
                    key={h}
                    className="text-[9px] sm:text-[10px] uppercase tracking-wider px-2.5 py-0.5 sm:py-1 rounded-full border border-white/15 bg-white/5 text-white/80 font-medium"
                  >
                    {h}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Call To Action Button */}
          <div className="mt-6 pt-2">
            <div
              data-testid={`server-join-${server.key}`}
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-5 py-2.5 rounded-full text-black text-xs sm:text-sm font-bold uppercase tracking-wider blocky-shadow-black border-2 border-black transition-transform duration-200 group-hover:translate-x-1"
              style={{ backgroundColor: themeColor }}
            >
              <span>Explore Server</span>
              <ArrowRight size={14} />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}